/**
 * CORE REDUCERS — le slice del prodotto, e i nomi che si portano dietro.
 *
 * Sta in un modulo suo e non dentro `store.ts` per una ragione sola: anche il
 * registro dei plugin deve poter leggere questi nomi, per rifiutare una slice
 * di plugin che ne occupi uno. Se vivessero in `store.ts` non potrebbe —
 * `store.ts` importa già `registry.ts` per raccogliere le slice dei plugin, e
 * il verso opposto chiuderebbe il ciclo.
 *
 * Da qui deriva anche `CORE_SLICE_NAMES`: derivato, non riscritto a mano, così
 * non può restare indietro rispetto ai reducer che descrive.
 */

import { authReducer } from '../auth/authSlice';
import { taskReducer } from '../entities/task/taskSlice';
import { noteReducer } from '../entities/note/noteSlice';
import { callReducer } from '../entities/call/callSlice';
import { emailReducer } from '../entities/email/emailSlice';
import { linkedinPostReducer } from '../entities/linkedinPost/linkedinPostSlice';
import { meetingReducer } from '../entities/meeting/meetingSlice';
import { queueReducer } from '../entities/queue/queueSlice';
import { personReducer } from '../entities/person/personSlice';
import { personSourceReducer } from '../entities/personSource/personSourceSlice';
import { personChannelReducer } from '../entities/personChannel/personChannelSlice';
import { rapidContactReducer } from '../entities/rapidContact/rapidContactSlice';
import { businessReducer } from '../entities/business/businessSlice';
import { relationshipReducer } from '../entities/relationship/relationshipSlice';
import { pipelineReducer } from '../entities/pipeline/pipelineSlice';
import { kanbanReducer } from '../entities/kanban/kanbanSlice';
import { quotationReducer } from '../entities/quotation/quotationSlice';
import { quotationFileReducer } from '../entities/quotationFile/quotationFileSlice';
import { quotationItemReducer } from '../entities/quotationItem/quotationItemSlice';
import { messageReducer } from '../entities/message/messageSlice';
import { activityReducer } from '../entities/activity/activitySlice';
import { whatsAppSessionReducer } from '../entities/whatsAppSession/whatsAppSessionSlice';
import { automationReducer } from '../entities/automation/automationSlice';
import { geographyReducer } from '../entities/geography/geographySlice';
import { auditLogReducer } from '../entities/auditLog/auditLogSlice';
import { themeReducer } from '../themeSlice';

export const coreReducers = {
  auth: authReducer,
  tasks: taskReducer,
  notes: noteReducer,
  calls: callReducer,
  emails: emailReducer,
  linkedinPosts: linkedinPostReducer,
  meetings: meetingReducer,
  queues: queueReducer,
  persons: personReducer,
  personSources: personSourceReducer,
  personChannels: personChannelReducer,
  rapidContacts: rapidContactReducer,
  businesses: businessReducer,
  relationships: relationshipReducer,
  pipelines: pipelineReducer,
  kanbans: kanbanReducer,
  // Il preventivo è entità di prodotto da TS-87: la chiave `quotations` è
  // del core, il plugin trentino tiene solo le proprie slice di estensione
  // (`tsQuotationDetails`, `tsQuotationItemDetails`).
  quotations: quotationReducer,
  quotationFiles: quotationFileReducer,
  quotationItems: quotationItemReducer,
  messages: messageReducer,
  activities: activityReducer,
  whatsAppSessions: whatsAppSessionReducer,
  automations: automationReducer,
  geography: geographyReducer,
  auditLog: auditLogReducer,
  theme: themeReducer,
};

/**
 * I nomi riservati al prodotto. `registerPlugin` li rifiuta a un plugin che
 * provi a usarli.
 *
 * Il controllo serve perché `createAppStore` fonde i due insiemi con uno
 * spread, e a parità di chiave **vince il plugin**: una slice di plugin
 * chiamata `quotations` sostituirebbe quella del core senza un errore, senza un
 * warning e senza che nulla nella UI lo faccia sospettare, se non il fatto che
 * metà prodotto smette di funzionare.
 */
export const CORE_SLICE_NAMES: readonly string[] = Object.keys(coreReducers);
